import type { UseMutationResult } from '@tanstack/react-query';
import { ZfCheckbox } from '@zafire/react';
import type { ColDef, ICellRendererParams, ITooltipParams } from 'ag-grid-enterprise';
import type { AxiosError } from 'axios';
import type { FunctionComponent } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClient } from '../../api';
import type { ItemSettingsDto } from '../../api/generated/client';
import { investmentStrategyMap } from '../../config/constants';
import type { ISetting } from '../../interfaces/simulation-settings';
import { formatAmount } from '../../utils/renderUtils';
import { formatPortfolioNumber } from '../../utils/utils';
import { AgGridTable, OverflowTooltip } from '../atoms';
import { RemarkTooltip } from './RemarkTooltip';
import { SettingBoxEmptyState } from './SettingBoxEmptyState';

interface IProps {
    itemSettings: ItemSettingsDto[];
    updateItemSettings: UseMutationResult<ItemSettingsDto, AxiosError, ISetting>;
    enabled: boolean;
    totalAmount: number;
    emptyStateMessage?: string;
    emptyStateShowWealthLink?: boolean;
    isUpdating?: boolean;
}

export const SettingBoxPositionTable: FunctionComponent<IProps> = ({
    itemSettings: initialItemSettings,
    updateItemSettings,
    enabled,
    totalAmount,
    emptyStateMessage,
    emptyStateShowWealthLink,
    isUpdating,
}: IProps) => {
    const { data: client } = useClient();
    const { t, i18n } = useTranslation();
    const [itemSettings, setItemSettings] = useState(initialItemSettings);

    useEffect(() => {
        if (initialItemSettings) {
            setItemSettings(initialItemSettings);
        }
    }, [initialItemSettings]);

    const onCheckBoxClick = (itemSetting: ItemSettingsDto, checked: boolean) => {
        const prevItemSettings = itemSettings;
        setItemSettings((prevState) => {
            const index = prevState.findIndex((item) => item.id === itemSetting.id);
            if (index !== -1) {
                const newItems = [...prevState];
                newItems[index] = {
                    ...newItems[index],
                    enabled: checked,
                };

                return newItems;
            }

            return prevState;
        });
        updateItemSettings.mutate(
            { ...itemSetting, enabled: checked },
            {
                onError: () => {
                    setItemSettings(prevItemSettings);
                },
            },
        );
    };

    const getCellLinesValue = (itemSetting: ItemSettingsDto) => {
        const investmentStrategy = itemSetting.investmentStrategy ? t(investmentStrategyMap[itemSetting.investmentStrategy]) : '';
        const portfolioNumber = formatPortfolioNumber(itemSetting.portfolioNumber, itemSetting.businessRelationId);
        const portfolioAndStrategyText = [portfolioNumber, investmentStrategy].join(' ');

        return { name: itemSetting.name, portfolioAndStrategyText };
    };

    const visibleItems = useMemo(() => itemSettings.filter((item) => !item.hidden), [itemSettings]);

    const tableData = useMemo<ItemSettingsDto[]>(
        () => [...visibleItems, { name: 'total', currency: client?.currency, amount: totalAmount }],
        [visibleItems, client?.currency, totalAmount],
    );

    if (visibleItems.length === 0 && emptyStateMessage)
        return <SettingBoxEmptyState message={emptyStateMessage} showWealthOverviewLink={emptyStateShowWealthLink} />;

    // Enforce-disabled rows expose their localized remark as a whole-row tooltip (all columns but the name,
    // which keeps its own overflow/full-name tooltip).
    const enforceDisabledRemark = ({ data }: ITooltipParams<ItemSettingsDto>) =>
        data?.enforceDisable ? (data.remarks?.[i18n.language] ?? data.remarks?.EN) : undefined;

    const columnDefs: ColDef<ItemSettingsDto>[] = [
        {
            width: 40,
            tooltipValueGetter: enforceDisabledRemark,
            cellRenderer: ({ data }: ICellRendererParams<ItemSettingsDto>) =>
                data?.name !== 'total' ? (
                    <ZfCheckbox
                        checked={data?.enabled}
                        disabled={!enabled || data?.enforceDisable}
                        onCheckboxCheck={(event: CustomEvent<boolean>) => onCheckBoxClick(data!, event.detail)}
                    />
                ) : null,
        },
        {
            field: 'name',
            headerName: t('description'),
            width: 200,
            cellRenderer: ({ data, value }: ICellRendererParams<ItemSettingsDto, string>) => {
                if (!value) return null;
                if (value === 'total') return <b>{t('total')}</b>;

                const { name, portfolioAndStrategyText } = getCellLinesValue(data!);

                return (
                    <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 'var(--type-body-small-line-height)' }}>
                        <OverflowTooltip
                            text={name ?? ''}
                            width={200}
                            padding={24}
                            styles={{ color: 'var(--fg-color-default-normal)', fontWeight: 'var(--type-body-small-bold-font-weight)' }}
                        />
                        <span>{portfolioAndStrategyText}</span>
                    </div>
                );
            },
        },
        {
            field: 'currency',
            headerName: t('currency'),
            width: 80,
            tooltipValueGetter: enforceDisabledRemark,
            cellRenderer: ({ data, value }: ICellRendererParams<ItemSettingsDto, string>) =>
                data?.name !== 'total' ? (value ?? '') : <b>{client?.currency}</b>,
        },
        {
            minWidth: 100,
            flex: 1,
            field: 'amount',
            headerName: t('amount'),
            headerClass: 'ag-right-aligned-header',
            tooltipValueGetter: enforceDisabledRemark,
            cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'end', textAlign: 'right' },
            cellRenderer: ({ data, value }: ICellRendererParams<ItemSettingsDto, number>) =>
                data?.name !== 'total' ? formatAmount(value ?? 0, false, '', true) : <b>{formatAmount(totalAmount, false, '', true)}</b>,
        },
    ];

    return (
        <div style={{ opacity: isUpdating ? 0.6 : 1, transition: 'opacity 150ms ease-in-out' }}>
            <AgGridTable<ItemSettingsDto>
                domLayout="autoHeight"
                rowData={tableData}
                columnDefs={columnDefs}
                getRowId={({ data }) => (data.id != null ? String(data.id) : String(data.name))}
                tooltipShowDelay={300}
                getRowStyle={({ data }) => ({
                    ...(data?.enabled ? { backgroundColor: 'var(--bg-color-default-strong)' } : undefined),
                    ...(data?.name === 'total' ? { borderBottom: 'var(--ag-row-border)' } : undefined),
                    ...(data?.enforceDisable ? { opacity: 0.5 } : undefined),
                })}
                defaultColDef={{
                    resizable: false,
                    sortable: false,
                    suppressHeaderMenuButton: true,
                    tooltipComponent: RemarkTooltip,
                    cellStyle: { display: 'flex', alignItems: 'center' },
                }}
                tableRowHeight={56}
                cellHorizontalPadding={12}
            />
        </div>
    );
};
